type PubMedInterventionConfig = {
    id: string
    sponsor: string[]
    label: string
    primaryTerm: string
    terms: string[]
}

type PubMedIndicationConfig = {
    id: string
    label: string
    aliases: string[]
    acronym?: string
    meshTerm?: string
    defaultDiseaseTerms: string[]
    defaultOutcomeTerms: string[]
    interventions: PubMedInterventionConfig[]
    demoFallbackInterventionIds?: string[]
}

// TODO Eventually we should add the * approaches for truncation/wildcards
const EFFICACY_OUTCOMES = [
    'efficacy',
    'effectiveness',
    'adverse events',
    'tolerability',

    // Add other outcomes from enrichment file
]

const ECONOMIC_OUTCOMES = [
    'cost',
    'cost-effective*',
    'cost-utility',
    'budget impact',
    'resource use',
    'hcru',
    'hospitali*ation', // TODO test 'hospitali*ation' for the wildcard of hospitalization
    'inpatient admissions',
    'healthcare utilization',
    'medication costs',
    'prescription costs',
    'Per member per month',
    'Work Productivity and Activity Impairment',
    // Add other outcomes from enrichment file
]

const HUMANISTIC_OUTCOMES = [
    'quality of life',
    'patient-reported outcomes',
    'SF-36',
    'EQ-5D*',
    // Add other outcomes from enrichment file
]

const WEIGHT_LOST = [
    'weight loss',
    'percent body weight change',
    'BMI',
    'cardiovascular outcomes',
    'MASH resolution',
    'fibrosis improvement',
    'QoL',
    'adherence',
    'persistence',
    'cost-effectiveness',
    'safety',
    'tolerability',
    'effectiveness',
    'economic outcomes',
    'economic burden',
    'body weight reduction',
    'waist circumference',
    'obesity-related complications',
    'MACE',
    'cardiovascular mortality',
    'all-cause mortality',
    'hospitalization',
    'physical functioning',
    'patient-reported outcomes',
    'treatment satisfaction',
    'healthcare resource utilization',
]

const TREATMENT_PATTERN_OUTCOMES = [
    'adherence',
    'persistence',
    'compliance',
    'discontinu*',
    'treatment switch',
    // Add other outcomes from enrichment file
]

const EPIDEMIOLOGY_OUTCOMES = [
    'incidence',
    'prevalence',
    // Add other outcomes from enrichment file
]

// The goal with the above would be to have a multi-tier selection where someone could choose general {efficacy, economic, treatment patterns, epidemiology, humanistic} in separate dropdowns and then have those automatically added.
// If we do this multi-tier selection I'd like them to be separated by ORs and grouped together so that we could possibly run separate queries and then log the number of hits we get for that specific search grouping.
// For example economic outcome terms had X number of search hits
// For disease-specific outcomes we could group them into these categories or just leave them as their own grouping or append them within efficacy and humanistic for the disease-specific efficacy outcomes or the disease-specific qol measures.

const indications: PubMedIndicationConfig[] = [
    // =========================
    // RHEUMATOID ARTHRITIS
    // =========================
    {
        id: 'rheumatoid_arthritis',
        label: 'Rheumatoid Arthritis',
        aliases: ['Rheumatoid Arthritis', 'Rheumatoid Arthritis (RA)'],
        acronym: 'RA',
        meshTerm: 'Arthritis, Rheumatoid',
        demoFallbackInterventionIds: ['humira', 'enbrel', 'remicade', 'rinvoq', 'xeljanz', 'actemra'],
        defaultDiseaseTerms: ['Rheumatoid Arthritis'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES, // TODO we probably don't need to repeat this over and over but instead grab this earlier
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'ACR20',
            'ACR50',
            'ACR70',
            'DAS28',
            'HAQ-DI',
            'radiographic progression',
            'remission',
            'disease activity',
            'FACIT*',
            'PROMIS Fatigue',
            'modified total Sharp score',
            'mTSS',
        ],
        interventions: [
            {
                id: 'humira',
                sponsor: ['AbbVie'],
                label: 'Humira (adalimumab)',
                primaryTerm: 'adalimumab',
                terms: ['Humira', 'adalimumab'],
            },
            {
                id: 'enbrel',
                sponsor: ['Amgen', 'Pfizer'],
                label: 'Enbrel (etanercept)',
                primaryTerm: 'etanercept',
                terms: ['Enbrel', 'etanercept'],
            },
            {
                id: 'remicade',
                sponsor: ['Johnson & Johnson'],
                label: 'Remicade (infliximab)',
                primaryTerm: 'infliximab',
                terms: ['Remicade', 'infliximab'],
            },
            {
                id: 'rinvoq',
                sponsor: ['AbbVie'],
                label: 'Rinvoq (upadacitinib) [ABT-494]',
                primaryTerm: 'upadacitinib',
                terms: ['Rinvoq', 'upadacitinib', 'ABT-494'],
            },
            {
                id: 'kineret',
                sponsor: ['sobi'],
                label: 'Kineret (anakinra)',
                primaryTerm: 'anakinra',
                terms: ['kineret', 'anakinra'],
            },
            {
                id: 'xeljanz',
                sponsor: ['Pfizer'],
                label: 'Xeljanz (tofacitinib)',
                primaryTerm: 'tofacitinib',
                terms: ['Xeljanz', 'tofacitinib'],
            },
            {
                id: 'actemra',
                sponsor: ['Roche'],
                label: 'Actemra (tocilizumab)',
                primaryTerm: 'tocilizumab',
                terms: ['Actemra', 'tocilizumab'],
            },
        ],
    },

    // =========================
    // IgA Nephropathy (IgAN)
    // =========================
    {
        id: 'igan',
        label: 'IgAN',
        aliases: ['IgA Nephropathy (IgAN)', 'IgAN', 'IgA Nephropathy'],
        acronym: 'IgAN',
        meshTerm: '',
        defaultDiseaseTerms: ['IgA Nephropathy', 'IgAN'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'FACIT*',
            'Quality of Life Questionnaire',
            'MEST-C',
        ],
        interventions: [
            {
                id: 'sibeprenlimab',
                sponsor: ['Otsuka'],
                label: 'Voyxact (sibeprenlimab) [VIS649]',
                primaryTerm: 'sibeprenlimab',
                terms: ['Voyxact', 'sibeprenlimab', 'VIS649'],
            },
            {
                id: 'vanrafia',
                sponsor: ['Novartis'],
                label: 'Vanrafia (atrasentan)',
                primaryTerm: 'atrasentan',
                terms: ['Vanrafia', 'atrasentan'],
            },
            {
                id: 'filspiri',
                sponsor: ['Travere Therapeutics'],
                label: 'Filspari (sparsentan)',
                primaryTerm: 'sparsentan',
                terms: ['Filspari', 'sparsentan'],
            },
            {
                id: 'tarpeyo',
                sponsor: ['Calliditas Therapeutics'],
                label: 'Tarpeyo (budesonide delayed release)',
                primaryTerm: 'budesonide',
                terms: ['Tarpeyo', 'budesonide'],
            },
            {
                id: 'fabhalta',
                sponsor: ['Novartis'],
                label: 'Fabhalta (iptacopan) [LNP023]',
                primaryTerm: 'iptacopan',
                terms: ['Fabhalta', 'iptacopan', 'LNP023'],
            },
        ],
    },

    // =========================
    // ATOPIC DERMATITIS
    // =========================
    {
        id: 'atopic_dermatitis',
        label: 'Atopic Dermatitis',
        aliases: ['Atopic Dermatitis', 'Atopic Dermatitis (AD)'],
        acronym: 'AD',
        meshTerm: 'Dermatitis, Atopic',
        defaultDiseaseTerms: ['Atopic Dermatitis'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'EASI',
            'eczema area and severity index',
            'IGA',
            'investigator global assessment',
            'pruritus',
            'itch',
            'DLQI',
            'dermatology life quality index',
        ],
        interventions: [
            {
                id: 'dupixent',
                sponsor: ['Sanofi', 'Regeneron'],
                label: 'Dupixent (dupilumab) [REGN668]',
                primaryTerm: 'dupilumab',
                terms: ['Dupixent', 'dupilumab', 'REGN668'],
            },
            {
                id: 'rinvoq',
                sponsor: ['AbbVie'],
                label: 'Rinvoq (upadacitinib) [ABT-494]',
                primaryTerm: 'upadacitinib',
                terms: ['Rinvoq', 'upadacitinib', 'ABT-494'],
            },
            {
                id: 'opzelura',
                sponsor: ['Incyte'],
                label: 'Opzelura (ruxolitinib)',
                primaryTerm: 'ruxolitinib',
                terms: ['Opzelura', 'ruxolitinib'],
            },
            {
                id: 'cibinqo',
                sponsor: ['Pfizer'],
                label: 'Cibinqo (abrocitinib)',
                primaryTerm: 'abrocitinib',
                terms: ['Cibinqo', 'abrocitinib'],
            },
            {
                id: 'ebglyss',
                sponsor: ['Eli Lilly'],
                label: 'Ebglyss (lebrikizumab)',
                primaryTerm: 'lebrikizumab',
                terms: ['Ebglyss', 'lebrikizumab'],
            },
            {
                id: 'adbry',
                sponsor: ['LEO Pharma'],
                label: 'Adbry (tralokinumab)',
                primaryTerm: 'tralokinumab',
                terms: ['Adbry', 'tralokinumab'],
            },
            {
                id: 'olumiant',
                sponsor: ['Eli Lilly'],
                label: 'Olumiant (baricitinib)',
                primaryTerm: 'baricitinib',
                terms: ['Olumiant', 'baricitinib'],
            },
            {
                id: 'eucrisa',
                sponsor: ['Pfizer'],
                label: 'Eucrisa (crisaborole)',
                primaryTerm: 'crisaborole',
                terms: ['Eucrisa', 'crisaborole'],
            },
            {
                id: 'elidel',
                sponsor: ['Meda'],
                label: 'Elidel (pimecrolimus)',
                primaryTerm: 'pimecrolimus',
                terms: ['Elidel', 'pimecrolimus'],
            },
            {
                id: 'protopic',
                sponsor: ['Astellas'],
                label: 'Protopic (tacrolimus)',
                primaryTerm: 'tacrolimus',
                terms: ['Protopic', 'tacrolimus'],
            },
        ],
    },

    // =========================
    // ASTHMA (BIOLOGICS)
    // =========================
    {
        id: 'asthma',
        label: 'Asthma',
        aliases: ['Asthma'],
        acronym: '',
        meshTerm: 'Asthma',
        defaultDiseaseTerms: ['Asthma'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'SGRQ',
            'FEV1',
            'forced expiratory volume',
            'exacerbation',
            'asthma control',
            'ACQ',
            'AQLQ',
            'rescue treatment',
            'PROMIS*',
        ],
        interventions: [
            {
                id: 'dupixent',
                sponsor: ['Sanofi', 'Regeneron'],
                label: 'Dupixent (dupilumab) [REGN668]',
                primaryTerm: 'dupilumab',
                terms: ['Dupixent', 'dupilumab', 'REGN668'],
            },
            {
                id: 'nucala',
                sponsor: ['GSK'],
                label: 'Nucala (mepolizumab)',
                primaryTerm: 'mepolizumab',
                terms: ['Nucala', 'mepolizumab'],
            },
            {
                id: 'fasenra',
                sponsor: ['AstraZeneca'],
                label: 'Fasenra (benralizumab)',
                primaryTerm: 'benralizumab',
                terms: ['Fasenra', 'benralizumab'],
            },
            {
                id: 'cinqair',
                sponsor: ['Teva'],
                label: 'Cinqair (reslizumab)',
                primaryTerm: 'reslizumab',
                terms: ['Cinqair', 'reslizumab'],
            },
            {
                id: 'tezspire',
                sponsor: ['Amgen', 'AstraZeneca'],
                label: 'Tezspire (tezepelumab)',
                primaryTerm: 'tezepelumab',
                terms: ['Tezspire', 'tezepelumab'],
            },
        ],
    },

    // =========================
    // CRSwNP
    // =========================
    {
        id: 'crswnp',
        label: 'Chronic Rhinosinusitis with Nasal Polyps',
        aliases: ['CRSwNP', 'Chronic Rhinosinusitis with Nasal Polyps'],
        acronym: 'CRSwNP',
        meshTerm: '',
        defaultDiseaseTerms: ['chronic rhinosinusitis with nasal polyps'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'nasal polyp score',
            'nasal congestion',
            'SNOT-22',
            'sinus opacification',
        ],
        interventions: [
            {
                id: 'dupixent',
                sponsor: ['Sanofi', 'Regeneron'],
                label: 'Dupixent (dupilumab)',
                primaryTerm: 'dupilumab',
                terms: ['Dupixent', 'dupilumab'],
            },
            {
                id: 'tezspire',
                sponsor: ['Amgen', 'AstraZeneca'],
                label: 'Tezspire (tezepelumab)',
                primaryTerm: 'tezepelumab',
                terms: ['Tezspire', 'tezepelumab'],
            },
            {
                id: 'fasenra',
                sponsor: ['AstraZeneca'],
                label: 'Fasenra (benralizumab)',
                primaryTerm: 'benralizumab',
                terms: ['Fasenra', 'benralizumab'],
            },
        ],
    },

    // =========================
    // COPD
    // =========================
    {
        id: 'copd',
        label: 'Chronic Obstructive Pulmonary Disease',
        aliases: ['COPD'],
        acronym: 'COPD',
        meshTerm: 'Pulmonary Disease, Chronic Obstructive',
        defaultDiseaseTerms: ['COPD'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'FEV1',
            'exacerbations',
            'hospitalization',
            'quality of life',
            'SGRQ',
            '6-MWT',
            'HAM-D',
            'Hospital Anxiety and Depression Scale',
        ],
        interventions: [
            {
                id: 'dupixent',
                sponsor: ['Sanofi', 'Regeneron'],
                label: 'Dupixent (dupilumab)',
                primaryTerm: 'dupilumab',
                terms: ['Dupixent', 'dupilumab'],
            },
            {
                id: 'nucala',
                sponsor: ['GSK'],
                label: 'Nucala (mepolizumab)',
                primaryTerm: 'mepolizumab',
                terms: ['Nucala', 'mepolizumab'],
            },
        ],
    },
    // =========================
    // Phenylketonuria
    // =========================
    {
        id: 'Phenylketonuria',
        label: 'Phenylketonuria (PKU)',
        aliases: ['PKU', 'Phenylketonuria'],
        acronym: 'PKU',
        meshTerm: '',
        defaultDiseaseTerms: ['Phenylketonuria', 'PKU'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'Wechsler Intelligence Scale for Children',
            'WISC',
            'FSIQ-WPPSI',
            'PKU-QOL',
        ],
        interventions: [
            {
                id: 'Repinatrabit',
                sponsor: ['Otsuka'],
                label: 'Repinatrabit [JNT-517]',
                primaryTerm: '',
                terms: ['Repinatrabit', 'JNT-517'],
            },
            {
                id: 'Sephience',
                sponsor: ['Biomarin'],
                label: 'Sephience (sepiapterin) [PTC923]',
                primaryTerm: 'sepiapterin',
                terms: ['sephience', 'sepiapterin', 'PTC923'],
            },
            {
                id: 'domofenogene zalfaparvovec',
                sponsor: ['Biomarin'],
                label: 'domofenogene zalfaparvovec [BMN 307]',
                primaryTerm: '',
                terms: ['domofenogene zalfaparvovec', 'BMN 307'],
            },
        ],
    },
    // =========================
    // Sjogren's Disease
    // =========================
    {
        id: 'sjd',
        label: 'SjD',
        aliases: ['SjD', "Sjogren's Disease", 'Sjögren’s Disease', "Sjogren's Syndrome", 'Sjögren’s Syndrome'],
        acronym: 'SjD',
        meshTerm: '',
        defaultDiseaseTerms: ["Sjogrens's Disease", "Sjogrens's Syndrome"],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'ESSDAI',
            'EULAR Sjogren*',
            'ESSPRI',
            "Sjogren's Syndrome Symptom Diary",
            'DiSSA',
            'ClinESSDAI',
            'BCVA',
            'FACIT*',
            'Fatigue',
            'salivary flow',
        ],
        interventions: [
            {
                id: 'ianalumab',
                sponsor: ['Novartis'],
                label: 'Ianalumab [VAY736]',
                primaryTerm: 'ianalumab',
                terms: ['ianalumab', 'VAY736'],
            },
            {
                id: 'dazodalibep',
                sponsor: ['Horizon Therapeutics', 'Amgen'],
                label: 'Dazodalibep [HZN-4920]',
                primaryTerm: 'dazodalibep',
                terms: ['dazodalibep', 'HZN-4920'],
            },
            {
                id: 'nipocalimab',
                sponsor: ['Johnson & Johnson'],
                label: 'Nipocalimab',
                primaryTerm: 'nipocalimab',
                terms: ['nipocalimab'],
            },
            {
                id: 'sibeprenlimab',
                sponsor: ['Otsuka'],
                label: 'Voyxact (sibeprenlimab) [VIS649]',
                primaryTerm: 'sibeprenlimab',
                terms: ['Voyxact', 'sibeprenlimab', 'VIS649'],
            },
        ],
    },
    // =========================
    // FUS-ALS
    // =========================
    {
        id: 'fus_als',
        label: 'FUS ALS',
        aliases: [
            'FUS ALS',
            'FUS-associated ALS',
            'FUS associated ALS',
            'FUS amyotrophic lateral sclerosis',
            'Amyotrophic Lateral Sclerosis, FUS-related',
            'ALS-FUS',
        ],
        acronym: 'FUS ALS',
        meshTerm: '',
        defaultDiseaseTerms: ['FUS ALS', 'FUS amyotrophic lateral sclerosis'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'ALSAQ-5',
            'ALSFRS',
            'ALS Functional Rating Scale',
            'VAFS',
            'Ventilation Assistance-Free Survival',
            'Slow vital capacity',
            'Hand-held dynamometry',
        ],
        interventions: [
            {
                id: 'jacifusen',
                sponsor: ['Ionis Pharmaceuticals'],
                label: 'Jacifusen [ION363]',
                primaryTerm: 'jacifusen',
                terms: ['jacifusen', 'ION363'],
            },
            {
                id: 'ulefnersen',
                sponsor: ['Otsuka'],
                label: 'ulefnersen',
                primaryTerm: 'ulefnersen',
                terms: ['ulefnersen'],
            },
        ],
    },
    // =========================
    // Major Depressive Disorder
    // =========================
    {
        id: 'major_depressive_disorder',
        label: 'MDD',
        aliases: ['Major Depressive Disorder', 'MDD', 'Major Depressive Disorder (MDD)'],
        acronym: 'MDD',
        meshTerm: 'Depressive Disorder, Major',
        defaultDiseaseTerms: ['Major Depressive Disorder'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'MADRS',
            'Montgomery-Asberg Depression Rating Scale',
            'Montgomery-Eisberg Depression Rating Scale',
            'HAM-D',
            'HAM-A',
            'Hamilton Depression',
            'Hamilton Anxiety',
            'Dimensional Anhedonia Rating',
            'anhedonia',
            'suicidal ideation',
            'Columbia-Suicide Severity Rating Scale',
            'C-SSRS',
            'PANSS',
            'PHQ-9',
            'Beck Depression Inventory',
            'BDI-II',
        ],
        interventions: [
            {
                id: 'auvelity',
                sponsor: ['Axsome Therapeutics'],
                label: 'Auvelity (dextromethorphan/bupropion)',
                primaryTerm: 'dextromethorphan bupropion',
                terms: ['Auvelity', 'dextromethorphan', 'bupropion', 'dextromethorphan bupropion'],
            },
            {
                id: 'rexulti',
                sponsor: ['Otsuka', 'Lundbeck'],
                label: 'Rexulti (brexpiprazole)',
                primaryTerm: 'brexpiprazole',
                terms: ['Rexulti', 'brexpiprazole'],
            },
            {
                id: 'solriamfetol',
                sponsor: ['Axsome Therapeutics'],
                label: 'solriamfetol',
                primaryTerm: 'solriamfetol',
                terms: ['solriamfetol', ''],
            },
            {
                id: 'spravato',
                sponsor: ['Johnson & Johnson'],
                label: 'Spravato (esketamine)',
                primaryTerm: 'esketamine',
                terms: ['Spravato', 'esketamine'],
            },
            {
                id: 'osavampator ',
                sponsor: ['Neurocrine Biosciences'],
                label: 'Osavampator [NBI-1065845]',
                primaryTerm: 'osavampator',
                terms: ['osavampator', 'NBI-1065845'],
            },
            {
                id: 'msp_2020',
                sponsor: ['Otsuka', 'Mindset Pharma'],
                label: 'MSP-2020',
                primaryTerm: 'MSP-2020',
                terms: ['MSP-2020'],
            },
            {
                id: 'vortioxetine',
                sponsor: ['Takeda', 'Lundbeck'],
                label: 'Trintellix (vortioxetine)',
                primaryTerm: 'vortioxetine',
                terms: ['Trintellix', 'vortioxetine'],
            },
        ],
    },
    // =========================
    // Generalized Anxiety Disorder
    // =========================
    {
        id: 'generalized_anxiety_disorder',
        label: 'GAD',
        aliases: ['GAD', 'Generalized Anxiety Disorder', 'Generalized Anxiety Disorder (GAD)'],
        acronym: 'GAD',
        meshTerm: '',
        defaultDiseaseTerms: ['Generalized Anxiety Disorder'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'HAM-A',
            'Hamilton Anxiety',
            'GAD-7',
            'generalized anxiety disorder-7',
            'Hospital Anxiety and Depression Scale',
            'Columbia-Suicide Severity Rating Scale',
            'C-SSRS',
        ],
        interventions: [
            {
                id: 'ulotaront',
                sponsor: ['Sumitomo Pharma', 'Otsuka'],
                label: 'ulotaront [SEP-363856]',
                primaryTerm: 'ulotaront',
                terms: ['ulotaront', 'SEP-363856'],
            },
            {
                id: 'iti_1284',
                sponsor: ['Intra-Cellular Therapies'],
                label: 'ITI-1284',
                primaryTerm: 'ITI-1284',
                terms: ['ITI-1284'],
            },
            {
                id: 'abbv_932',
                sponsor: ['AbbVie'],
                label: 'ABBV-932',
                primaryTerm: 'ABBV-932',
                terms: ['ABBV-932'],
            },
            {
                id: 'lysergide',
                sponsor: ['Definium Therapeutics', 'MindMed'],
                label: 'MM120 (lysergide) [LSD D-tartrate]',
                primaryTerm: 'lysergide',
                terms: ['MM120', 'lysergide', 'LSD D-tartrate'],
            },
        ],
    },
    // =========================
    // Rett Syndrome
    // =========================
    {
        id: 'rett_syndrome',
        label: 'Rett Syndrome',
        aliases: ['Rett Syndrome'],
        acronym: 'RTT',
        meshTerm: '',
        defaultDiseaseTerms: ['Rett Syndrome'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'CSBS-DP*',
            'Communication and Symbolic Behavior Scales Developmental',
            'ICND',
            'Childhood Neurologic Disability',
            'Rett Syndrome Behaviour Questionnaire',
            'RSBQ',
            'RTT-CBI',
            'RTT-COMC',
            'RTT-HF',
            'RTT-VCOM',
            'Rett Syndrome Caregiver Burden Inventory',
            'Observer-Reported Communication Ability',
            'PROMIS',
            'CaGI-C',
            'CMS-28',
        ],
        interventions: [
            {
                id: 'tsha_102',
                sponsor: ['Taysha Gene Therapies'],
                label: 'TSHA-102',
                primaryTerm: 'TSHA-102',
                terms: ['TSHA-102'],
            },
            {
                id: 'trofinetide',
                sponsor: ['Acadia Pharmaceuticals'],
                label: 'DAYBUE (trofinetide)',
                primaryTerm: 'trofinetide',
                terms: ['DAYBUE', 'DAYBUE STIX', 'trofinetide', 'NNZ-2566'],
            },
            {
                id: 'ngn_401',
                sponsor: ['Neurogene'],
                label: 'NGN-401',
                primaryTerm: 'NGN-401',
                terms: ['NGN-401'],
            },
        ],
    },
    {
        id: 'fabry_disease',
        label: 'Fabry Disease',
        aliases: ['Fabry Disease', 'Alpha-Galactosidase A Deficiency', 'GLA Deficiency'],
        meshTerm: 'Fabry Disease',
        defaultDiseaseTerms: ['Fabry Disease', 'Anderson-Fabry Disease'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'Fabry-specific Pediatric Health and Pain Questionnaire',
            'FPHPQ',
            'FABPRO*',
            'Mainz Severity Score Index',
            'PedsQL*',
        ],
        interventions: [
            {
                id: 'fabrazyme',
                sponsor: ['Sanofi'],
                label: 'Fabrazyme (agalsidase beta)',
                primaryTerm: 'agalsidase beta',
                terms: ['Fabrazyme', 'agalsidase beta'],
            },
            {
                id: 'replagal',
                sponsor: ['Takeda'],
                label: 'Replagal (agalsidase alfa)',
                primaryTerm: 'agalsidase alfa',
                terms: ['Replagal', 'agalsidase alfa'],
            },
            {
                id: 'galafold',
                sponsor: ['Amicus Therapeutics'],
                label: 'Galafold (migalastat)',
                primaryTerm: 'migalastat',
                terms: ['Galafold', 'migalastat'],
            },
            {
                id: 'prx_102',
                sponsor: ['Chiesi', 'Protalix BioTherapeutics'],
                label: 'Elfabrio (pegunigalsidase alfa) [PRX-102]',
                primaryTerm: 'pegunigalsidase alfa',
                terms: ['Elfabrio', 'pegunigalsidase alfa', 'PRX-102'],
            },
            {
                id: 'venglustat',
                sponsor: ['Sanofi'],
                label: 'Venglustat',
                primaryTerm: 'venglustat',
                terms: ['venglustat', 'GZ402671'],
            },
            {
                id: 'lucerastat',
                sponsor: ['Idorsia Pharmaceuticals'],
                label: 'Lucerastat',
                primaryTerm: 'lucerastat',
                terms: ['lucerastat'],
            },
            {
                id: 'isaralgagene_civaparvovec',
                sponsor: ['Sangamo Therapeutics'],
                label: 'Isaralgagene civaparvovec (ST-920)',
                primaryTerm: 'isaralgagene civaparvovec',
                terms: ['isaralgagene civaparvovec', 'ST-920'],
            },
            {
                id: 'flt190',
                sponsor: ['Freeline Therapeutics'],
                label: 'FLT190',
                primaryTerm: 'FLT190',
                terms: ['FLT190'],
            },
            {
                id: '4d_310',
                sponsor: ['4D Molecular Therapeutics'],
                label: '4D-310',
                primaryTerm: '4D-310',
                terms: ['4D-310'],
            },
        ],
    },
    // =========================
    // Eosinophilic Esophagitis
    // =========================
    {
        id: 'eosinophilic_esophagitis',
        label: 'EoE',
        aliases: ['Eosinophilic Esophagitis', 'Eosinophilic Esophagitis (EoE)', 'EoE'],
        acronym: 'EoE',
        meshTerm: 'Eosinophilic Esophagitis',
        defaultDiseaseTerms: ['Eosinophilic Esophagitis'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'EREFS',
            'eosinophil count',
            'peak eosinophil count',
            'EoE-HSS',
            'DSQ',
            'Dysphagia Symptom Questionnaire',
            'Eosinophilic Esophagitis Endoscopic Reference Score',
            'PEESS',
            'EoE-IQ',
        ],
        interventions: [
            {
                id: 'dupixent',
                sponsor: ['Sanofi', 'Regeneron'],
                label: 'Dupixent (dupilumab)',
                primaryTerm: 'dupilumab',
                terms: ['Dupixent', 'dupilumab', 'REGN668'],
            },
            {
                id: 'eohilia',
                sponsor: ['Takeda'],
                label: 'Eohilia (budesonide oral suspension) [TAK-721]',
                primaryTerm: 'budesonide',
                terms: ['Eohilia', 'budesonide oral suspension', 'budesonide', 'TAK-721'],
            },
            {
                id: 'cendakimab',
                sponsor: ['Bristol Myers Squibb'],
                label: 'Cendakimab (CC-93538)',
                primaryTerm: 'cendakimab',
                terms: ['cendakimab', 'CC-93538'],
            },
            {
                id: 'lirentelimab',
                sponsor: ['Allakos'],
                label: 'Lirentelimab (AK002)',
                primaryTerm: 'lirentelimab',
                terms: ['lirentelimab', 'AK002'],
            },
        ],
    },
    // =========================
    // Prurigo Nodularis
    // =========================
    {
        id: 'prurigo_nodularis',
        label: 'PN',
        aliases: ['PN', 'Prurigo Nodularis', 'Prurigo Nodularis (PN)'],
        acronym: 'PN',
        meshTerm: 'Prurigo',
        defaultDiseaseTerms: ['Prurigo Nodularis'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'WI-NRS',
            'Worst Itch Numeric Rating Scale',
            'Itch NRS',
            'pruritus',
            'PP-NRS',
            'peak pruritus',
            'AP-NRS',
            'average pruritus',
            'IGA-CPG*',
            'IGA-CNPG*',
            'Prurigo Activity Score',
            'DLQI',
            'Dermatology Life Quality Index',
            'Investigator Global Assessment',
            'nodule count',
            'skin lesions',
            'sleep disturbance',
            'Sleep Quality NRS',
            'Hospital Anxiety and Depression Scale',
            'FACIT*',
            'WP-NRS',
            'Worst Pain Numeric Rating Scale',
        ],
        interventions: [
            {
                id: 'dupixent',
                sponsor: ['Sanofi', 'Regeneron'],
                label: 'Dupixent (dupilumab)',
                primaryTerm: 'dupilumab',
                terms: ['Dupixent', 'dupilumab', 'REGN668'],
            },
            {
                id: 'nemluvio',
                sponsor: ['Galderma'],
                label: 'Nemluvio (nemolizumab)',
                primaryTerm: 'nemolizumab',
                terms: ['Nemluvio', 'nemolizumab', 'nemolizumab-ilto', 'CD14152'],
            },
            {
                id: 'vixarelimab',
                sponsor: ['Genentech', 'Roche', 'Kiniksa Pharmaceuticals'],
                label: 'Vixarelimab (KPL-716)',
                primaryTerm: 'vixarelimab',
                terms: ['vixarelimab', 'KPL-716'],
            },
            {
                id: 'povorcitinib',
                sponsor: ['Incyte'],
                label: 'Povorcitinib (INCB054707)',
                primaryTerm: 'povorcitinib',
                terms: ['povorcitinib', 'INCB054707'],
            },
        ],
    },
    // =========================
    // Bullous Pemphigoid
    // =========================
    {
        id: 'bullous_pemphigoid',
        label: 'BP',
        aliases: ['BP', 'Bullous Pemphigoid', 'Bullous Pemphigoid (BP)'],
        acronym: 'BP',
        meshTerm: 'Pemphigoid, Bullous',
        defaultDiseaseTerms: ['Bullous Pemphigoid'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
        ],
        interventions: [
            {
                id: 'dupixent',
                sponsor: ['Sanofi', 'Regeneron'],
                label: 'Dupixent (dupilumab)',
                primaryTerm: 'dupilumab',
                terms: ['Dupixent', 'dupilumab', 'REGN668'],
            },
        ],
    },

    // =========================
    // Chronic Spontaneous Urticaria
    // =========================
    {
        id: 'chronic_spontaneous_urticaria',
        label: 'CSU',
        aliases: ['CSU', 'Chronic Spontaneous Urticaria', 'Chronic Spontaneous Urticaria (CSU)'],
        acronym: 'CSU',
        meshTerm: 'Urticaria',
        defaultDiseaseTerms: ['Chronic Spontaneous Urticaria'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'DLQI',
            'dermatology life quality index',
            'UAS7',
            'Urticaria Activity Score',
            'ISS7',
            'Itch Severity Score',
            'HSS7',
            'Hives Severity Score',
            'AAS7',
            'Angioedema Activity Score',
            'UCT',
            'Urticaria Control Test',
            'complete control',
            'loss of control',
        ],
        interventions: [
            {
                id: 'xolair',
                sponsor: ['Novartis', 'Genentech', 'Roche'],
                label: 'Xolair (omalizumab)',
                primaryTerm: 'omalizumab',
                terms: ['Xolair', 'omalizumab'],
            },
            {
                id: 'dupixent',
                sponsor: ['Sanofi', 'Regeneron'],
                label: 'Dupixent (dupilumab)',
                primaryTerm: 'dupilumab',
                terms: ['Dupixent', 'dupilumab', 'REGN668'],
            },
            {
                id: 'rhapsido',
                sponsor: ['Novartis'],
                label: 'Rhapsido (remibrutinib)',
                primaryTerm: 'remibrutinib',
                terms: ['Rhapsido', 'remibrutinib', 'LOU064'],
            },
            {
                id: 'ligelizumab',
                sponsor: ['Novartis'],
                label: 'Ligelizumab (QGE031)',
                primaryTerm: 'ligelizumab',
                terms: ['ligelizumab', 'QGE031'],
            },
            {
                id: 'barzolvolimab',
                sponsor: ['Celldex Therapeutics'],
                label: 'Barzolvolimab (CDX-0159)',
                primaryTerm: 'barzolvolimab',
                terms: ['barzolvolimab', 'CDX-0159'],
            },
        ],
    },
    // =========================
    // OBESITY / WEIGHT LOSS
    // =========================
    {
        id: 'weight_loss',
        label: 'Obesity / Weight Loss',
        aliases: ['Obesity', 'Weight Loss', 'Weight Management', 'Overweight and Obesity'],
        acronym: '',
        meshTerm: 'Obesity',
        defaultDiseaseTerms: ['Obesity', 'Weight Loss', 'Weight Management'],
        defaultOutcomeTerms: [
            ...WEIGHT_LOST,
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'IWQOL-Lite',
            'Impact of Weight on Quality of Life',
        ],
        interventions: [
            {
                id: 'wegovy',
                sponsor: ['Novo Nordisk'],
                label: 'Wegovy (semaglutide)',
                primaryTerm: 'semaglutide',
                terms: ['Wegovy', 'semaglutide'],
            },
            {
                id: 'zepbound',
                sponsor: ['Eli Lilly'],
                label: 'Zepbound (tirzepatide)',
                primaryTerm: 'tirzepatide',
                terms: ['Zepbound', 'tirzepatide'],
            },
            {
                id: 'saxenda',
                sponsor: ['Novo Nordisk'],
                label: 'Saxenda (liraglutide)',
                primaryTerm: 'liraglutide',
                terms: ['Saxenda', 'liraglutide'],
            },
            {
                id: 'qsymia',
                sponsor: ['Vivus'],
                label: 'Qsymia (phentermine/topiramate)',
                primaryTerm: 'phentermine topiramate',
                terms: ['Qsymia', 'phentermine', 'topiramate', 'phentermine topiramate'],
            },
            {
                id: 'contrave',
                sponsor: ['Currax'],
                label: 'Contrave (naltrexone/bupropion)',
                primaryTerm: 'naltrexone bupropion',
                terms: ['Contrave', 'naltrexone', 'bupropion', 'naltrexone bupropion'],
            },
            {
                id: 'xenical',
                sponsor: ['Roche'],
                label: 'Xenical (orlistat)',
                primaryTerm: 'orlistat',
                terms: ['Xenical', 'orlistat'],
            },
            {
                id: 'imcivree',
                sponsor: ['Rhythm Pharmaceuticals'],
                label: 'Imcivree (setmelanotide)',
                primaryTerm: 'setmelanotide',
                terms: ['Imcivree', 'setmelanotide'],
            },
            {
                id: 'retatrutide',
                sponsor: ['Eli Lilly'],
                label: 'Retatrutide (LY3437943)',
                primaryTerm: 'retatrutide',
                terms: ['retatrutide', 'LY3437943', 'LY-3437943'],
            },
            {
                id: 'orforglipron',
                sponsor: ['Eli Lilly'],
                label: 'Orforglipron (LY3502970)',
                primaryTerm: 'orforglipron',
                terms: ['orforglipron', 'LY3502970', 'LY-3502970'],
            },
            {
                id: 'cagrisema',
                sponsor: ['Novo Nordisk'],
                label: 'CagriSema (cagrilintide/semaglutide)',
                primaryTerm: 'cagrilintide semaglutide',
                terms: ['CagriSema', 'cagrilintide', 'semaglutide', 'cagrilintide semaglutide'],
            },
            {
                id: 'amycretin',
                sponsor: ['Novo Nordisk'],
                label: 'Amycretin',
                primaryTerm: 'amycretin',
                terms: ['amycretin'],
            },
            {
                id: 'survodutide',
                sponsor: ['Boehringer Ingelheim', 'Zealand Pharma'],
                label: 'Survodutide',
                primaryTerm: 'survodutide',
                terms: ['survodutide', 'BI 456906', 'BI-456906'],
            },
            {
                id: 'mazdutide',
                sponsor: ['Innovent Biologics', 'Eli Lilly'],
                label: 'Mazdutide',
                primaryTerm: 'mazdutide',
                terms: ['mazdutide', 'IBI362', 'LY3305677'],
            },
            {
                id: 'pemvidutide',
                sponsor: ['Altimmune'],
                label: 'Pemvidutide',
                primaryTerm: 'pemvidutide',
                terms: ['pemvidutide', 'ALT-801', 'ALT801'],
            },
        ],
    },
    // =========================
    // Maple syrup urine disease
    // =========================
    {
        id: 'maple_syrup_urine_disease',
        label: 'MSUD',
        aliases: [
            'MSUD',
            'Maple Syrup Urine Disease',
            'Maple Syrup Urine Disease (MSUD)',
            'Branched-Chain Ketoaciduria',
            'Branched Chain Ketoaciduria',
        ],
        acronym: 'MSUD',
        meshTerm: 'Maple Syrup Urine Disease',
        defaultDiseaseTerms: ['Maple Syrup Urine Disease', 'MSUD'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'encephalopathy',
            'metabolic decompensation',
            'dietary restriction',
            'liver transplant',
        ],
        interventions: [
            {
                id: 'Repinatrabit',
                sponsor: ['Otsuka'],
                label: 'Repinatrabit [JNT-517]',
                primaryTerm: 'Repinatrabit',
                terms: ['Repinatrabit', 'JNT-517'],
            },
        ],
    },
    // =========================
    // Drug-Resistant Tuberculosis
    // =========================
    {
        id: 'drug_resistant_tuberculosis',
        label: 'DR-TB',
        aliases: ['Drug-Resistant Tuberculosis', 'Drug Resistant Tuberculosis', 'Drug-Resistant Tuberculosis (DR-TB)'],
        acronym: 'DR-TB',
        meshTerm: 'Tuberculosis, Multidrug-Resistant',
        defaultDiseaseTerms: ['Drug-Resistant Tuberculosis', 'DR-TB'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
        ],
        interventions: [
            {
                id: 'quabodepistat',
                sponsor: ['Otsuka'],
                label: 'Quabodepistat (OPC-167832)',
                primaryTerm: 'Quabodepistat',
                terms: ['Quabodepistat', 'OPC-167832'],
            },
        ],
    },
    // =========================
    // Urea Cycle Disorders
    // =========================
    {
        id: 'urea_cycle_disorders',
        label: 'UCD',
        aliases: ['Urea Cycle Disorder', 'Urea Cycle Disorders', 'Urea Cycle Disorders (UCD)'],
        acronym: 'UCD',
        meshTerm: 'Urea Cycle Disorders, Inborn',
        defaultDiseaseTerms: ['Urea Cycle Disorders', 'ornithine transcarbamylase deficiency'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
        ],
        interventions: [
            {
                id: 'Repinatrabit',
                sponsor: ['Otsuka'],
                label: 'Repinatrabit [JNT-517]',
                primaryTerm: 'Repinatrabit',
                terms: ['Repinatrabit', 'JNT-517'],
            },
            {
                id: 'ravicti',
                sponsor: ['Horizon Therapeutics', 'Amgen'],
                label: 'Ravicti (glycerol phenylbutyrate)',
                primaryTerm: 'glycerol phenylbutyrate',
                terms: ['Ravicti', 'glycerol phenylbutyrate', 'HPN-100'],
            },
            {
                id: 'buphenyl',
                sponsor: ['Horizon Therapeutics', 'Amgen'],
                label: 'Buphenyl (sodium phenylbutyrate)',
                primaryTerm: 'sodium phenylbutyrate',
                terms: ['Buphenyl', 'sodium phenylbutyrate', 'phenylbutyrate', 'NaPBA'],
            },
            {
                id: 'olpruva',
                sponsor: ['Acer Therapeutics', 'Relief Therapeutics'],
                label: 'Olpruva (sodium phenylbutyrate) [ACER-001]',
                primaryTerm: 'sodium phenylbutyrate',
                terms: ['Olpruva', 'ACER-001', 'sodium phenylbutyrate'],
            },
            {
                id: 'dtx301',
                sponsor: ['Ultragenyx'],
                label: 'DTX301 (avalotcagene ontaparvovec)',
                primaryTerm: 'avalotcagene ontaparvovec',
                terms: ['DTX301', 'avalotcagene ontaparvovec'],
            },
        ],
    },
    // =========================
    // Ovarian Cancer
    // =========================
    {
        id: 'ovarian_cancer',
        label: 'Ovarian Cancer',
        aliases: ['Ovarian Cancer'],
        meshTerm: 'Ovarian Neoplasms',
        defaultDiseaseTerms: ['Ovarian Cancer'],
        defaultOutcomeTerms: [
            ...EFFICACY_OUTCOMES,
            ...ECONOMIC_OUTCOMES,
            ...HUMANISTIC_OUTCOMES,
            ...TREATMENT_PATTERN_OUTCOMES,
            ...EPIDEMIOLOGY_OUTCOMES,
            'EORTC QLQ-C30',
            'EORTC QLQ-OV28',
            'FACT-G',
            'functional assessment of cancer therapy general',
            'Eastern Cooperative Oncology Group performance status',
            'ECOG PS',
            'Functional Assessment of Cancer Therapy-Ovarian Symptom Index',
            'NCCN-FACT Ovarian Symptom Index',
            'NFOSI-18',
        ],
        interventions: [
            {
                id: 'lynparza',
                sponsor: ['AstraZeneca', 'Merck'],
                label: 'Lynparza (olaparib)',
                primaryTerm: 'olaparib',
                terms: ['Lynparza', 'olaparib', 'AZD2281'],
            },
            {
                id: 'zejula',
                sponsor: ['GSK'],
                label: 'Zejula (niraparib)',
                primaryTerm: 'niraparib',
                terms: ['Zejula', 'niraparib', 'MK-4827'],
            },
            {
                id: 'dostarlimab',
                sponsor: ['GSK'],
                label: 'Jemperli (dostarlimab)',
                primaryTerm: 'dostarlimab',
                terms: ['Jemperli', 'dostarlimab', 'TSR-042'],
            },
            {
                id: 'relacorilant',
                sponsor: ['Corcept Therapeutics'],
                label: 'Relacorilant',
                primaryTerm: 'relacorilant',
                terms: ['relacorilant', 'CORT125134'],
            },
        ],
    },
]

export const pubmedIndicationConfig = {
    indications,
}

export type { PubMedIndicationConfig, PubMedInterventionConfig }
